import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8007';

const Footer = () => {
  const [lastUpdated, setLastUpdated] = useState(new Date().toISOString());
  const [backendStatus, setBackendStatus] = useState('checking');

  // Check backend connection status
  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        // This would be replaced with actual backend health check
        const response = await fetch(`${API_BASE_URL}/health`, {
          method: 'GET',
          timeout: 5000
        });
        
        if (response.ok) {
          setBackendStatus('connected');
        } else {
          setBackendStatus('error');
        }
      } catch (error) {
        setBackendStatus('disconnected');
      }
    };

    // Initial check
    checkBackendStatus();

    // Set up periodic health checks every 30 seconds
    const healthCheckInterval = setInterval(checkBackendStatus, 30000);

    // Update the last updated timestamp every minute
    const timestampInterval = setInterval(() => {
      setLastUpdated(new Date().toISOString());
    }, 60000);

    return () => {
      clearInterval(healthCheckInterval);
      clearInterval(timestampInterval);
    };
  }, []);

  // Get status indicator
  const getStatusIndicator = () => {
    switch (backendStatus) {
      case 'connected':
        return {
          color: '#10b981',
          text: 'Connected',
          icon: (
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )
        };
      case 'disconnected':
        return {
          color: '#ef4444',
          text: 'Disconnected',
          icon: (
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )
        };
      case 'error':
        return {
          color: '#f59e0b',
          text: 'Error',
          icon: (
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )
        };
      default:
        return {
          color: '#6b7280',
          text: 'Checking...',
          icon: (
            <svg viewBox="0 0 20 20" fill="currentColor" className="animate-spin">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
          )
        };
    }
  };

  // Format date for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const status = getStatusIndicator();

  return (
    <footer className="app-footer" role="contentinfo">
      <div className="footer-container">
        {/* Left section - Version and copyright */}
        <div className="footer-left">
          <div className="footer-version">
            <span className="version-label">Venturi IMS</span>
            <span className="version-number">v1.0.0</span>
          </div>
          <div className="footer-copyright">
            <p>© {new Date().getFullYear()} LeanGains. All rights reserved.</p>
          </div>
        </div>

        {/* Center section - Links */}
        <div className="footer-center">
          <nav className="footer-nav" aria-label="Footer navigation">
            <Link to="/documentation" className="footer-link">
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
              </svg>
              Documentation
            </Link>
            <Link to="/support" className="footer-link">
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-1.506 1.506A5.98 5.98 0 004 10c0-.867.157-1.698.44-2.467l1.44 1.44a3.997 3.997 0 00-.722 2.194zm8.49-2.943l1.524 1.525A5.987 5.987 0 0016 10c0-1.007-.25-1.958-.693-2.787l-1.44-1.44a4.015 4.015 0 00.78 1.487z" clipRule="evenodd" />
              </svg>
              Support
            </Link>
            <Link to="/privacy" className="footer-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="footer-link">
              Terms of Service
            </Link>
          </nav>
        </div>

        {/* Right section - Status and timestamp */}
        <div className="footer-right">
          <div className="status-indicator">
            <div 
              className="status-dot"
              style={{ color: status.color }}
              aria-label={`Backend status: ${status.text}`}
            >
              {status.icon}
            </div>
            <span className="status-text">
              Backend: {status.text}
            </span>
          </div>
          <div className="last-updated">
            <span className="update-label">Last updated:</span>
            <time 
              dateTime={lastUpdated}
              className="update-time"
              title={new Date(lastUpdated).toString()}
            >
              {formatTimestamp(lastUpdated)}
            </time>
          </div>
        </div>
      </div>

      {/* Mobile footer - simplified layout */}
      <div className="footer-mobile">
        <div className="footer-mobile-row">
          <div className="footer-mobile-version">
            Venturi IMS v1.0.0
          </div>
          <div className="footer-mobile-status">
            <div 
              className="status-dot-mobile"
              style={{ color: status.color }}
              aria-label={`Backend status: ${status.text}`}
            >
              {status.icon}
            </div>
            <span>{status.text}</span>
          </div>
        </div>
        
        <div className="footer-mobile-row">
          <div className="footer-mobile-copyright">
            © {new Date().getFullYear()} LeanGains
          </div>
          <div className="footer-mobile-links">
            <Link to="/privacy" className="footer-mobile-link">Privacy</Link>
            <Link to="/terms" className="footer-mobile-link">Terms</Link>
            <Link to="/support" className="footer-mobile-link">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;